/**
 * CampaignRow component for displaying campaigns with expandable ad sets
 */

import { ChevronRight, ChevronDown, Loader2, Target, DollarSign, Video } from 'lucide-react';
import { useState } from 'react';

import { useAdSets } from '@/hooks/useMetaBrowse';
import { cn } from '@/lib/utils';
import type { MetaCampaign, MetaDateRange } from '@/types/meta';

import { AdSetRow } from './AdSetRow';

interface CampaignRowProps {
  campaign: MetaCampaign;
  teamId: string;
  dateRange?: MetaDateRange;
  selectedAds: Set<string>;
  onSelectAd: (adId: string, selected: boolean) => void;
}

export function CampaignRow({
  campaign,
  teamId,
  dateRange,
  selectedAds,
  onSelectAd,
}: CampaignRowProps) {
  const [expanded, setExpanded] = useState(false);

  // Only fetch ad sets when expanded
  const { data: adSets, isLoading } = useAdSets({
    teamId,
    campaignId: expanded ? campaign.id : null,
    dateRange,
    enabled: expanded,
  });

  // Format spend for display
  const formatSpend = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="border-b border-border-subtle last:border-b-0">
      {/* Campaign Header */}
      <button
        onClick={handleToggle}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-4 text-left',
          'hover:bg-surface-subtle transition-colors'
        )}
      >
        {/* Expand/Collapse */}
        {expanded ? (
          <ChevronDown className="h-5 w-5 text-muted flex-shrink-0" />
        ) : (
          <ChevronRight className="h-5 w-5 text-muted flex-shrink-0" />
        )}

        {/* Campaign Icon */}
        <Target className="h-5 w-5 text-brand flex-shrink-0" />

        {/* Campaign Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-primary truncate">
              {campaign.name}
            </span>
            <span className={cn(
              'text-xs px-1.5 py-0.5 rounded',
              campaign.status === 'ACTIVE'
                ? 'bg-success-soft text-success'
                : 'bg-surface-subtle text-muted'
            )}>
              {campaign.status}
            </span>
          </div>
          {campaign.objective && (
            <span className="text-xs text-muted capitalize">
              {campaign.objective.toLowerCase().replace(/_/g, ' ')}
            </span>
          )}
        </div>

        {/* Metrics */}
        <div className="flex items-center gap-6 text-sm">
          {/* Spend */}
          <div className="flex items-center gap-1.5">
            <DollarSign className="h-4 w-4 text-muted" />
            <span className="font-medium text-secondary">
              {formatSpend(campaign.spend)}
            </span>
          </div>

          {/* Video Count */}
          <div className="flex items-center gap-1.5">
            <Video className="h-4 w-4 text-muted" />
            <span className="font-medium text-secondary">
              {campaign.videoAdCount}
            </span>
            <span className="text-xs text-muted">videos</span>
          </div>

          {/* Ad Set Count */}
          <span className="text-xs text-muted">
            {campaign.adSetCount} ad set{campaign.adSetCount !== 1 ? 's' : ''}
          </span>
        </div>
      </button>

      {/* Expanded Ad Sets */}
      {expanded && (
        <div className="bg-surface-subtle/30">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 text-muted animate-spin" />
              <span className="ml-2 text-sm text-muted">Loading ad sets...</span>
            </div>
          ) : adSets && adSets.length > 0 ? (
            adSets.map((adSet) => (
              <AdSetRow
                key={adSet.id}
                adSet={adSet}
                teamId={teamId}
                selectedAds={selectedAds}
                onSelectAd={onSelectAd}
              />
            ))
          ) : (
            <div className="py-6 text-center text-sm text-muted">
              No ad sets in this campaign
            </div>
          )}
        </div>
      )}
    </div>
  );
}
